import { Controller } from "@hotwired/stimulus"

// Amount entry for "Add to savings" — a pared-back twin of goal_form.
//
// Same 40px display field, same thousands separators, same self-measuring
// width, same preset rail. What it drops is everything goal_form needs and one
// row does not: no dial, no preview card, no second screen. The fold itself is
// native <details>, so opening and closing needs no JavaScript at all.
export default class extends Controller {
  static targets = ["display", "sizer", "field", "chip", "submit"]

  static values = { locale: String }

  connect() {
    this.amount = parseInt(this.fieldTarget.value, 10) || 0
    this.#fit()
    this.#refitOnFontLoad()
    this.render()
  }

  // The fold opening is the moment this screen is asking for a number, so the
  // keyboard comes with it. The measure has to happen here too: inside a shut
  // fold the sizer has no box, and a zero measurement would pin the field at
  // 6px for good.
  folded() {
    if (!this.element.open) return

    this.#fit()
    this.displayTarget.focus()
  }

  // The whole row is a tap target for the keyboard; the pencil says so.
  focusAmount() {
    this.displayTarget.focus()
  }

  typeAmount(event) {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 9)

    this.amount = digits ? parseInt(digits, 10) : 0
    event.target.value = digits ? this.#withSeparators(this.amount) : ""
    this.#fit()
    this.render()
  }

  // A preset chip is a shortcut to a round number, through the same door.
  pickAmount(event) {
    this.amount = parseInt(event.currentTarget.dataset.amount, 10) || 0
    this.displayTarget.value = this.amount ? this.#withSeparators(this.amount) : ""
    this.#fit()
    this.render()
  }

  render() {
    this.fieldTarget.value = this.amount || ""

    this.chipTargets.forEach((chip) => {
      chip.dataset.on = String(parseInt(chip.dataset.amount, 10) === this.amount)
    })

    // Nothing to add without an amount, so the button stays greyed.
    if (this.hasSubmitTarget) this.submitTarget.disabled = this.amount <= 0
  }

  // ── Internals ──

  // The field is widened to its own text so the pencil stays beside the number
  // instead of drifting with it. Lifted from goal_form#fitAmount.
  #fit() {
    const input = this.displayTarget

    this.sizerTarget.textContent = input.value || input.placeholder
    // getBoundingClientRect keeps the sub-pixel width; offsetWidth rounds to a
    // whole pixel and the last glyph loses whatever fraction it was owed.
    const width = this.sizerTarget.getBoundingClientRect().width
    if (!width) return

    input.style.width = `${Math.ceil(width) + 6}px`
  }

  #refitOnFontLoad() {
    document.fonts?.ready.then(() => {
      if (this.element.isConnected) this.#fit()
    })
  }

  #withSeparators(value) {
    return value.toLocaleString(this.localeValue)
  }
}
